import { useCallback, useState } from 'react';

import { ArtifactUnpacked } from '@/services/types/artifact-types';

export const useArtifactSelection = () => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const selectArtifact = useCallback((id: string) => {
    setSelectedIds((prev) => new Set(prev).add(id));
  }, []);

  const deselectArtifact = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  }, []);

  const toggleArtifact = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const selectMultiple = useCallback((ids: string[]) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      ids.forEach((id) => newSet.add(id));
      return newSet;
    });
  }, []);

  const selectAll = useCallback((artifacts: ArtifactUnpacked[]) => {
    setSelectedIds(new Set(artifacts.map((a) => a.id)));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const isSelected = useCallback(
    (id: string) => {
      return selectedIds.has(id);
    },
    [selectedIds],
  );

  const getSelectedArtifacts = useCallback(
    (artifacts: ArtifactUnpacked[]) => {
      return artifacts.filter((artifact) => selectedIds.has(artifact.id));
    },
    [selectedIds],
  );

  return {
    selectedIds,
    selectedCount: selectedIds.size,
    selectArtifact,
    deselectArtifact,
    toggleArtifact,
    selectMultiple,
    selectAll,
    clearSelection,
    isSelected,
    getSelectedArtifacts,
  };
};
